import React from 'react';
import { useTheme } from '@mui/material';
import { Box, Flex } from 'pounceV2';
import { SETTINGS_CONTENT_MAX_WIDTH } from './constants';
import { preloadSettingsChunks } from './preloadSettingsChunks';
import SettingsPageHeaderV3 from './SettingsPageHeader/SettingsPageHeaderV3';
import SettingsSidebarV3 from './SettingsSidebar/SettingsSidebarV3';
import { useActiveSettingsItem } from './useActiveSettingsItem';

interface SettingsShellV3Props {
  children: React.ReactNode;
}

/**
 * Full-viewport overlay wrapping every `/settings/*` route. Renders
 * the persistent left sidebar, the page header, and the routed page
 * content in a single bounded column so every sub-page is aligned.
 *
 * `AuthenticatedRoutesFallback` (routes.tsx) returns `null` on
 * settings paths so lazy sub-route swaps don't paint over the shell.
 */
const SettingsShellV3: React.FC<SettingsShellV3Props> = ({ children }) => {
  const {
    palette: { tokens },
  } = useTheme();
  const active = useActiveSettingsItem();

  React.useEffect(() => {
    preloadSettingsChunks();
  }, []);

  return (
    <Flex
      direction="row"
      height="100vh"
      width="100vw"
      backgroundColor={tokens.background.neutral.canvas.base}
      data-tid="settings-shell"
    >
      <SettingsSidebarV3 activeItemId={active?.item.id ?? null} />
      <Box
        as="main"
        flex={1}
        minWidth={0}
        height="100vh"
        backgroundColor={tokens.background.neutral.canvas.base}
        // `scrollbar-gutter: stable` reserves the scrollbar track even
        // on short pages so width never shifts ~15px between sub-pages.
        sx={{ overflowY: 'auto', scrollbarGutter: 'stable' }}
      >
        {/* `px={8}` is 32px each side; the +64 keeps the usable content
            width at `SETTINGS_CONTENT_MAX_WIDTH` after padding. Dropping
            the +64 narrows every settings page by 64px. */}
        <Box mx="auto" px={8} pt={8} pb={12} maxWidth={SETTINGS_CONTENT_MAX_WIDTH + 64}>
          {active && (
            <SettingsPageHeaderV3
              category={active.group.label}
              title={active.item.label}
              subheader={active.item.subheader}
              docsHref={active.item.docsHref}
            />
          )}
          {children}
        </Box>
      </Box>
    </Flex>
  );
};

export default SettingsShellV3;
